// DateRangeSelector.jsx
// react
import { useState } from 'react';

// internationalization
// * i18n
import { useTranslation } from 'react-i18next';

// material ui icons
// * Icons
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// material ui
// * MUI
import { Box, Button, Chip, Popover, Stack, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// relative
// * React Datepicker
import './datepicker.scss';

// * Hooks
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import {
  DatePicker,
  formatDate,
  normalizeRangeToZone,
  parseTimestamp,
  shortcutsItems,
  ZONE,
} from './datePickerUtils';
import { DatePickerStyles } from './DateRangePicker';

// * Luxon
import { DateTime } from 'luxon';

const DateRangeSelector = ({
  toKey,
  fromKey,
  defaultFrom,
  defaultTo,
  showReset = false,
}) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'));

  // Parse defaults directly during render; no local sync effect needed.
  const parsedDefaultFrom = parseTimestamp(defaultFrom);
  const parsedDefaultTo = parseTimestamp(defaultTo);

  // Initialize the hook with proper defaults
  const { from, to, range, setRange } = useUrlDateRange({
    zone: 'Europe/Copenhagen',
    defaultDays: 30,
    toKey,
    fromKey,
    // Disable auto defaults when we have specific default timestamps
    autoSetDefaults: !(parsedDefaultFrom && parsedDefaultTo),
  });

  // Derive effective range (pure computation)
  const effectiveRange = (() => {
    if (from && to) return [from, to];
    if (parsedDefaultFrom && parsedDefaultTo) {
      return [parsedDefaultFrom, parsedDefaultTo];
    }
    return range;
  })();

  // Derive temp dates from current range (no useState needed)
  const currentTempDates = (() => {
    const [start, end] = normalizeRangeToZone(effectiveRange);
    return [start ? start.toJSDate() : null, end ? end.toJSDate() : null];
  })();

  // Local state for picker interaction (only when popover is open)
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Use effective temp dates (current temp state when open, or derived from range when closed)
  const displayTempStartDate = anchorEl ? tempStartDate : currentTempDates[0];
  const displayTempEndDate = anchorEl ? tempEndDate : currentTempDates[1];

  // Event handlers
  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleShortcutClick = (shortcut) => {
    const [start, end] = shortcut.getValue();
    setTempStartDate(start.toJSDate());
    setTempEndDate(end.toJSDate());
  };

  const handleConfirm = () => {
    if (!tempStartDate || !tempEndDate) return;

    const start = DateTime.fromJSDate(tempStartDate).setZone(ZONE);
    const end = DateTime.fromJSDate(tempEndDate).setZone(ZONE);

    setRange(start, end, {
      roundToDay: true,
      clampToNow: true,
    });

    setAnchorEl(null);
  };

  const handleReset = () => {
    if (!parsedDefaultFrom || !parsedDefaultTo) return;

    setTempStartDate(parsedDefaultFrom.toJSDate());
    setTempEndDate(parsedDefaultTo.toJSDate());

    setRange(parsedDefaultFrom, parsedDefaultTo, {
      roundToDay: true,
      clampToNow: true,
    });

    setAnchorEl(null);
  };

  const handleClick = (event) => {
    // Initialize temp dates from current range when opening popover
    const [start, end] = currentTempDates;
    setTempStartDate(start);
    setTempEndDate(end);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    // Reset temp state when closing
    setTempStartDate(null);
    setTempEndDate(null);
  };

  return (
    <>
      <DatePickerStyles theme={theme} />

      <Tooltip
        title={t('common.select_period')}
        enterDelay={500}
        placement="top"
      >
        <Button
          startIcon={<CalendarTodayRoundedIcon />}
          variant="text"
          onClick={handleClick}
          disableRipple
          sx={{
            ':hover': { background: 'none' },
          }}
        >
          {!isSmall && effectiveRange[0] && effectiveRange[1]
            ? `${formatDate(effectiveRange[0])} - ${formatDate(effectiveRange[1])}`
            : !isSmall
              ? t('common.select_period')
              : null}
        </Button>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box
          className="custom-datepicker-wrapper"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Main Content - Shortcuts and Date Picker side by side */}
          <Box sx={{ display: 'flex' }}>
            {/* Shortcuts */}
            <Box
              sx={{
                p: 2,
                minWidth: 160,
              }}
            >
              <Stack direction="column" spacing={1}>
                {shortcutsItems.map((shortcut) => (
                  <Chip
                    key={shortcut.label}
                    label={shortcut.label}
                    onClick={() => handleShortcutClick(shortcut)}
                    size="medium"
                    sx={{
                      cursor: 'pointer',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Date Picker */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <DatePicker
                selected={displayTempStartDate}
                onChange={handleDateChange}
                startDate={displayTempStartDate}
                endDate={displayTempEndDate}
                selectsRange
                inline
                monthsShown={1}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                locale={'da'}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{
              p: 2,
              backgroundColor: 'background.paper',
            }}
          >
            {/* Reset Button (left side) */}
            {showReset && parsedDefaultFrom && parsedDefaultTo && (
              <Button
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                variant="outlined"
              >
                {t('actions.reset')}
              </Button>
            )}

            {/* Spacing div when no reset button */}
            {!(showReset && parsedDefaultFrom && parsedDefaultTo) && <div />}

            {/* Confirm Button (right side) */}
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={!displayTempStartDate || !displayTempEndDate}
            >
              {t('actions.submit')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeSelector;
